import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Group, Stack, Title } from "@mantine/core";
import { useCallbackRef } from "@mantine/hooks";

import { StatementStatus } from "@/billing/components/StatementsTab/constants/statements";
import { useOpenStatementsSearch } from "@/billing/hooks/useOpenStatementsSearch";
import { useOpenStatementsTableData } from "@/billing/hooks/useOpenStatementsTableData";
import { toast } from "@/utils/notifications";

import OpenStatementsSearch from "./OpenStatementsSearch";
import OpenStatementsStatusFilter from "./OpenStatementsStatusFilter";
import OpenStatementsTable from "./OpenStatementsTable";

const OPEN_STATEMENTS_ERROR_TOAST_ID = "open-statements-load-error";

interface OpenStatementsProps {
  refreshKey?: number;
  onLoadingChange?: (loading: boolean) => void;
}

export default function OpenStatements({ refreshKey = 0, onLoadingChange }: OpenStatementsProps) {
  const { practiceId } = useParams<{ practiceId: string }>();
  const [statuses, setStatuses] = useState<StatementStatus[]>([]);
  const filters = useMemo(() => ({ statuses }), [statuses]);
  const { rows, isLoading, error } = useOpenStatementsTableData(practiceId, refreshKey, filters);
  const { query, setQuery, searchOption, setSearchOption, filteredRows, isResolvingPayers } =
    useOpenStatementsSearch(rows);
  const onLoadingChangeCallback = useCallbackRef(onLoadingChange);

  useEffect(() => {
    onLoadingChangeCallback?.(isLoading);
  }, [isLoading, onLoadingChangeCallback]);

  useEffect(() => {
    return () => onLoadingChangeCallback?.(false);
  }, [onLoadingChangeCallback]);

  useEffect(() => {
    if (!error) return;
    // Reuse one id so a retry replaces the toast instead of stacking a new one.
    toast({
      id: OPEN_STATEMENTS_ERROR_TOAST_ID,
      title: "Could not load open statements",
      message: "Please try again. If the error persists, contact support.",
    });
  }, [error]);

  return (
    <Stack gap="lg">
      <Group w="100%" align="center" justify="space-between">
        <Group gap="sm" align="center">
          <Title order={2}>Open Statements</Title>
          <OpenStatementsStatusFilter
            statuses={statuses}
            onChange={setStatuses}
            targetTestId="open-statements-scope-filter"
          />
        </Group>
        <OpenStatementsSearch
          query={query}
          onQueryChange={setQuery}
          searchOption={searchOption}
          onSearchOptionChange={setSearchOption}
        />
      </Group>
      <OpenStatementsTable
        error={error}
        rows={filteredRows}
        isLoading={isLoading}
        refreshKey={refreshKey}
        isSearching={!!query.trim()}
        isFiltered={statuses.length > 0}
        isResolvingPayers={isResolvingPayers}
      />
    </Stack>
  );
}
